import React, { useContext, useEffect, useState } from "react";
import { mdiClipboardCheck } from "@mdi/js";
import intl from "react-intl-universal";
import styled from "styled-components";

import plotUtils from "../../../../../../script/utils/plotUtils";
import calcAreaUtils from "../../../../../../script/utils/calcAreaUtils";

import { AreaItem } from "./AreaItem";
import { Grid } from "../../../../../components/Grid";
import { Spacer } from "../../../../../components/Spacer";
import { Button } from "../../../../../components/Button";
import { BoxList } from "../../../../../components/BoxList";
import { EntryContext } from "../../../../../contexts/EntryContext";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

export const AreasLayer = props => {
    const { plot, show } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { calcAreas } = entry.calcSetting;

    const layerName = "areasLayer";
    const areaRectName = "areaRect";
    const areaBoundaryName = "areaBoundary";

    const [selectedIndex, setSelectedIndex] = useState();
    const [selectedGroup, setSelectedGroup] = useState();

    useEffect(() => {
        const layer = plot.addLayer(layerName, 150);
        layer.onDeactivate(() => layer.removeObjects());
        const feature = plot.applyRectFeature(layer, "rgba(255,0,0,0.3)", true);
        feature.onSelect(group => {
            onSelect(group?.index);
            setSelectedGroup(group);
        });
        feature.onModify(group => {
            const entry = entryContext.getEntry();
            const calcArea = entry.calcSetting.calcAreas.filter(ca => ca.name !== "OVERVIEW")[group.index];
            if (calcArea !== undefined) {
                const news = calcAreaUtils.calculateAreaFromGroup(
                    group,
                    group.getObjects().find(o => o.mxwellName === areaRectName),
                    group.getObjects().find(o => o.mxwellName === areaBoundaryName),
                    plot
                );
                if (calcAreaUtils.isVertical(calcArea)) {
                    news.vaxisX = 0;
                    news.vaxisY = 0;
                }
                Object.assign(calcArea, news);
                entryContext.setEntry(entry);
                setTimeout(() => {
                    onSelect(group.index);
                }, 1);
            }
        });
    }, []);

    useEffect(() => {
        if (show) {
            plot.getLayer(layerName).activate();
            drawAreas();
        } else {
            setSelectedIndex();
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            drawAreas();
        }
    }, [JSON.stringify(calcAreas)]);

    const drawAreas = () => {
        const layer = plot.getLayer(layerName);
        plotUtils.addCalcAreas(calcAreas, plot, layer, {
            evented: true,
            selectable: true,
            hasControls: true,
            selectedIndex,
        });
    };

    const addArea = () => {
        if (selectedGroup !== undefined) {
            const ca = calcAreaUtils.calculateArea(
                selectedGroup,
                plot
            );
            // the name input only validates while this layer is shown, so a new area must not start without one
            ca.name = calcAreaUtils.nextAreaName(calcAreas);
            calcAreas.push(ca);
            entryContext.setEntry(entry);
            onSelect(calcAreas.length - 1);
        }
    };

    const onSelect = index => {
        setSelectedIndex(index);
        if (index === undefined) {
            return;
        }
        plot.getLayer(layerName).getObjects()
            .forEach(group => {
                if (group.index === index) {
                    plot.canvas.setActiveObject(group);
                    plot.canvas.bringObjectToFront(group);
                }
            });
        plot.canvas.renderAll();
    };

    const onDelete = index => {
        const area = calcAreas.filter(ca => ca.name !== "OVERVIEW")[index];
        calcAreas.splice(calcAreas.indexOf(area), 1);
        entryContext.setEntry(entry);
    };

    const getRectangleForCalcAreaIndex = index => {
        const objs = plot.getLayer(layerName)?.getObjects();
        return objs?.find(r => r.index === index);
    };

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button
                    svgIcon={mdiClipboardCheck}
                    disabled={selectedGroup === undefined}
                    onClick={() => addArea()}
                    value={intl.get("entry.plan.areas.add")} />
            </Grid>
            <Spacer variant="horizontal" size="small" />
            {show &&
                <BoxList
                    selectedIndex={selectedIndex}
                    onClose={onDelete}
                    onSelect={onSelect}>
                    {calcAreas
                        .filter(calcArea => calcArea.name !== "OVERVIEW")
                        .map((calcArea, index) => (
                            <AreaItem
                                key={index}
                                plot={plot}
                                index={index}
                                calcArea={calcArea}
                                rectangle={getRectangleForCalcAreaIndex(index)} />
                        ))}
                </BoxList>
            }
        </LayerStyle>
    );
};